import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsArrowLeftRight } from 'react-icons/bs';
import { FaPlus } from 'react-icons/fa';
import { useToast } from '../contexts/ToastContext';
import { capitalAmortizationService } from '../services/capitalAmortizationService';
import { AmortizationOperation, AmortizationOperationRequest } from '../types/CapitalAmortization';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import ActionButton from '../components/common/ActionButton';
import OperacionForm from '../components/amortizaciones/OperacionForm';
import { formatCurrency } from '../utils/format';
import { apiErrorMessage, fmtDate } from './amortizacionesUtils';
import './Amortizaciones.css';

const Amortizaciones: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [operaciones, setOperaciones] = useState<AmortizationOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    capitalAmortizationService.getOperaciones()
      .then(res => setOperaciones(res.data))
      .catch(() => showToast('Error al cargar las operaciones de amortización', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const handleCreate = async (data: AmortizationOperationRequest) => {
    setSaving(true);
    try {
      const res = await capitalAmortizationService.createOperacion(data);
      showToast('Operación creada');
      navigate(`/amortizaciones/${res.data.id}`);
    } catch (err) {
      showToast(apiErrorMessage(err, 'Error al crear la operación'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="amz-page">
      <div className="amz-page-header">
        <p className="amz-intro">
          Amortizaciones anticipadas de capital en productos CRV y PH. Cada operación fija la fecha de corte y el
          criterio de reparto; después se negocia con cada inversor cuánto amortiza y si lo reinvierte o se le devuelve.
        </p>
        {!showForm && (
          <ActionButton icon={FaPlus} text="Nueva operación" variant="primary" onClick={() => setShowForm(true)} />
        )}
      </div>

      {showForm && (
        <div className="amz-panel">
          <h3>Nueva operación de amortización</h3>
          <OperacionForm saving={saving} onSubmit={handleCreate} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {loading && <LoadingSpinner />}

      {!loading && operaciones.length === 0 && !showForm && (
        <EmptyState icon={BsArrowLeftRight} title="Sin operaciones de amortización"
                    subtitle="Crea una operación para proponer y negociar amortizaciones con los inversores de un producto." />
      )}

      {!loading && operaciones.length > 0 && (
        <div className="amz-panel amz-table-wrap">
          <table className="amz-table amz-table-clickable">
            <thead>
              <tr>
                <th>Fecha de corte</th>
                <th>Producto</th>
                <th>Descripción</th>
                <th className="num">Inversores</th>
                <th>Negociación</th>
                <th className="num">Acordado</th>
                <th className="num">Ejecutado</th>
              </tr>
            </thead>
            <tbody>
              {operaciones.map(op => (
                <tr key={op.id} onClick={() => navigate(`/amortizaciones/${op.id}`)}>
                  <td>{fmtDate(op.fechaCorte)}</td>
                  <td>{op.productId} · {op.productName}</td>
                  <td>{op.descripcion}</td>
                  <td className="num">{op.numInversores}</td>
                  <td>
                    <span className="amz-counts">
                      <span className="amz-badge amz-badge-propuesta">{op.numPropuestas} prop.</span>
                      <span className="amz-badge amz-badge-acordada">{op.numAcordadas} acord.</span>
                      <span className="amz-badge amz-badge-ejecutada">{op.numEjecutadas} ejec.</span>
                      {op.numDescartadas > 0 && <span className="amz-badge amz-badge-descartada">{op.numDescartadas} desc.</span>}
                    </span>
                  </td>
                  <td className="num">{formatCurrency(op.capitalAcordado, 0)}</td>
                  <td className="num">{formatCurrency(op.capitalEjecutado, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Amortizaciones;
